// PSTUDIO-3866: read/write helpers for the transient group-color drag preview
// held on `tableStore.groupColorPreview`.
//
// Why a per-group `ObservableMap` rather than a single scalar slot: the table
// is a top-level MobX `observer`, and a scalar field notifies EVERY subscriber
// on any write — so during a custom-color drag each pointermove frame would
// re-render every visible group header and subrow guide, not just the group
// being dragged (O(N) churn). Keying the preview by groupId means a reader that
// does `map.get(myGroupId)` subscribes only to ITS key: writes to another
// group's key never notify it. Only one picker is open at a time, so at most one
// key is ever populated — the map just isolates the subscription granularity.
//
// Why extract these as pure functions over the map: the full store pulls lit and
// cannot be instantiated in the vitest node env, so the reaction-count contract
// (a write to group A must not notify a reader of group B) is unit-tested here
// against a bare `observable.map`, per `feedback_mobx_incremental_update_churn`.

import type { ObservableMap } from 'mobx';

/**
 * The live preview color for `groupId`, or `null` when no drag is in flight for
 * it. Reading a single key subscribes the caller to ONLY that key.
 */
export function readGroupColorPreview(
  preview: ObservableMap<string, string>,
  groupId: string
): string | null {
  return preview.get(groupId) ?? null;
}

/**
 * Set or clear the transient preview for `groupId`.
 *
 * - `color == null` clears the preview via `delete(groupId)` — inherently
 *   scoped to that key, so clearing group A can never wipe group B's in-flight
 *   preview (an unmount / close cleanup keyed to the row's own id is safe even
 *   while another group is mid-drag). Deleting an absent key is a MobX no-op.
 * - An unchanged (same color already stored) write is guarded out so it stays a
 *   MobX no-op — zero re-render churn on a stationary-but-still-firing frame.
 */
export function writeGroupColorPreview(
  preview: ObservableMap<string, string>,
  groupId: string,
  color: string | null
): void {
  if (color == null) {
    preview.delete(groupId);
    return;
  }
  if (preview.get(groupId) !== color) {
    preview.set(groupId, color);
  }
}
